import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Card, Col, Container, Modal, Row, Form } from "react-bootstrap"
import { addReport, deleteReport, editReport, getSucursals, importSucursalsFromEbroker } from "../../actions/other/sucursalsActions"
import { RootState, AppDispatch } from "../../store"
import { CustomDataTable } from "../../components/design/Datatables/CustomDataTable"
import { useAuth } from "../../components/auth/useAuth"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"
import SucursalReportForm from "../../components/forms/SucursalReportForm"

export interface Sucursal {
    _id?: string
    descripcion?: string
    ebrokerId?: number
    poblacion?: string
    provincia?: string
    informes?: []
}

interface SucursalReportState {
    sucursalId?: string
    sucursalDescription?: string
    reportId?: string
    reportDescription?: string
    reportIframe?: string
    informes?: any[]
}

export default function SucursalManagement() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch: AppDispatch = useDispatch()

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [search, setSearch] = useState<string>("")
    const [searchAux, setSearchAux] = useState('')
    const [totalRows, setTotalRows] = useState<number>(0)
    const [modalShowReport, setModalShowReport] = useState(false)
    const [formStateReport, setFormStateReport] = useState<SucursalReportState>({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isEditing, setIsEditing] = useState(false)


    const { sucursals, resultsuscursal } = useSelector((state: RootState) => state.sucursals)

    useEffect(() => {
        if (clientauth) {
            dispatch(getSucursals() as any)
        }
    }, [dispatch, t, clientauth])

    const handleImportSucursals = () => {
        Swal.fire({
            icon: 'question',
            title: t('sucursalsTranslate.importSucursals')!,
            text: t('sucursalsTranslate.importSucursalsAreYouSure')!,
            showDenyButton: true,
            confirmButtonText: t('global.ok')!,
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(importSucursalsFromEbroker())
            }
        })
    }

    const selectSucursalReport = (sucursal: Sucursal) => {
        setFormStateReport({
            sucursalId: sucursal._id,
            sucursalDescription: sucursal.descripcion,
            reportId: '',
            reportDescription: '',
            reportIframe: '',
            informes: sucursal.informes
        })
        setModalShowReport(true)
    }

    const handleInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        setFormStateReport({
            ...formStateReport,
            [target.name]: target.value
        })
    }

    const handleEditReport = (report: any) => {
        setIsEditing(true);
        setFormStateReport(prev => ({
            ...prev,
            reportId: report._id,
            reportDescription: report.titulo,
            reportIframe: report.iframe,
            titulo: report.titulo,
            iframe: report.iframe
        }));
        setModalShowReport(true);
    }

    const handleDeleteReport = async (sucursal: any, report: any) => {
        Swal.fire({
            showConfirmButton: true,
            showDenyButton: true,
            confirmButtonText: t('global.yes'),
            denyButtonText: t('global.no'),
            icon: 'question',
            text: t('documentTranslate.deleteDocumentsQuestion')
        }).then((resultuser) => {
            if (resultuser.isConfirmed) {
                dispatch(deleteReport(sucursal ?? '', report) as any)
            }
        })
    }

    const handleSubmitReport = async (e: any) => {
        e.preventDefault()
        setIsRegistering(true)
        if (isEditing) {
            dispatch(editReport(formStateReport.sucursalId ?? '', formStateReport.reportId ?? '', formStateReport) as any)
        }
        else {
            dispatch(addReport(formStateReport.sucursalId ?? '', formStateReport) as any)
        }
        setIsRegistering(false)
        setIsEditing(false);
    }

    useEffect(() => {
        if (resultsuscursal) {
            let resultsuscursalnow = resultsuscursal
            dispatch({ type: 'SUCURSAL_RESET_RESULT' })
            switch (resultsuscursalnow) {
                case 'success':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('documentTranslate.addsuccess')
                    }).then(() => {
                        setModalShowReport(false)
                        setIsEditing(false)
                    })
                    break;
                case 'successDeleteReport':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('documentTranslate.deleted')
                    }).then(() => {
                        setModalShowReport(false)
                    })
                    break;
                case 'successEditReport':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('documentTranslate.editSuccess')
                    }).then(() => {
                        setModalShowReport(false)
                        setIsEditing(false)
                    })
                    break;
                default:
                    break;
            }
        }
        // eslint-disable-next-line
    }, [resultsuscursal])

    const columnsSucursals = [
        {
            name: t("sucursalsTranslate.ebrokerId"),
            selector: (sucursal: Sucursal) => sucursal.ebrokerId?.toString() || "-",
            sortable: true,
            maxWidth: "120px",
            center: true,
        },
        {
            name: t("sucursalsTranslate.descripcion"),
            selector: (sucursal: Sucursal) => sucursal.descripcion || "",
            sortable: true,
            wrap: true,
        },
        {
            name: t("sucursalsTranslate.poblacion"),
            selector: (sucursal: Sucursal) => sucursal.poblacion || "-",
            sortable: true,
            maxWidth: "160px",
        },
        {
            name: t("sucursalsTranslate.provincia"),
            selector: (sucursal: Sucursal) => sucursal.provincia || "-",
            sortable: true,
            maxWidth: "140px",
        },
        {
            name: t("sucursalsTranslate.informes"),
            selector: (sucursal: Sucursal) => (
                <div className="cursor" onClick={() => selectSucursalReport(sucursal)}>
                    <i className="fas fa-paperclip"></i> {sucursal.informes && sucursal.informes.length > 0 ? sucursal.informes.length : ''}
                </div>
            ),
            maxWidth: "100px",
            center: true
        }
    ]


    return (
        <Container className='p-1 py-5 p-md-5'>
            <Row>
                <Col xs={12}>
                    <h1>{t('menus.sucursals')}</h1>
                </Col>
                <Col xs={12} className='d-flex justify-content-end'>
                    <button className="btn btn-primary" onClick={handleImportSucursals}>
                        {t("sucursalsTranslate.importSucursals")}
                    </button>
                </Col>
            </Row>
            <Row>
                <Col xs={12} className='my-3'>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    {sucursals?.length > 0 ? (
                                        <CustomDataTable
                                            columns={columnsSucursals}
                                            data={sucursals}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            searchText={search}
                                            onChangeRowsPerPage={(newLimit: number) => {
                                                setLimit(newLimit);
                                                setPage(1);
                                            }}
                                            onChangePage={(newPage: number) => {
                                                setPage(newPage);
                                            }}
                                        />
                                    ) : (
                                        <h4 className="mt-4">{t("sucursalsTranslate.thereisnotsucursals")}</h4>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Modal
                show={modalShowReport}
                onHide={() => {
                    setModalShowReport(false)
                    setIsEditing(false)
                }}
                aria-labelledby="contained-modal-title-vcenter-clip"
                fullscreen
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter-clip">
                        <h2 className='m-0'>POWER BI -{formStateReport?.sucursalDescription}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SucursalReportForm
                        state={formStateReport}
                        handleInputChange={handleInputChange}
                        handleDeleteReport={handleDeleteReport}
                        handleEditReport={handleEditReport}
                        submit={handleSubmitReport}
                        isEditing={isEditing}
                        isRegistering={isRegistering}
                    />
                </Modal.Body>
            </Modal>
        </Container>
    )
}